import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import { partiesAPI, itemsAPI, purchasesAPI } from '../../services/part2-api';
import BarcodeScanner from '../../components/BarcodeScanner';
import QuickAddItemModal from '../../components/QuickAddItemModal';
import PrintPreviewModal from '../../components/PrintPreviewModal';
import PremiumDocumentLayout from '../../components/documents/PremiumDocumentLayout';
import DocumentHeaderBar from '../../components/documents/DocumentHeaderBar';
import DocumentPartyPicker from '../../components/documents/DocumentPartyPicker';
import DocumentItemsTable from '../../components/documents/DocumentItemsTable';
import DocumentTotalsPanel from '../../components/documents/DocumentTotalsPanel';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FileText } from 'lucide-react';

const CreatePurchaseInvoice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { activeBusiness } = useBusiness();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [savedInvoiceId, setSavedInvoiceId] = useState(null);

  const [formData, setFormData] = useState({
    invoice_no: '',
    doc_date: new Date().toISOString().split('T')[0],
    due_date: '',
    party: null,
    meta: {
      reference_no: '',
      place_of_supply: '',
      payment_terms: 'Net 30'
    },
    items: [],
    totals: {
      subtotal: 0,
      discount_total: 0,
      taxable_amount: 0,
      tax_total: 0,
      round_off: 0,
      grand_total: 0
    },
    terms: {
      notes_public: '',
      terms_lines: ['Payment due within 30 days']
    }
  });

  const [partySearch, setPartySearch] = useState('');
  const [partyResults, setPartyResults] = useState([]);
  const [itemSearch, setItemSearch] = useState('');
  const [itemResults, setItemResults] = useState([]);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [enableRoundOff, setEnableRoundOff] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit) {
      loadInvoice();
    } else {
      generateInvoiceNumber();
    }
  }, [id]);

  useEffect(() => {
    calculateTotals();
  }, [formData.items, enableRoundOff]);

  const generateInvoiceNumber = () => {
    const num = `PINV-${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`;
    setFormData(prev => ({ ...prev, invoice_no: num }));
  };

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const response = await purchasesAPI.getInvoice(id);
      setFormData(response.data);
    } catch (error) {
      console.error('Error loading invoice:', error);
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const searchParties = async (term) => {
    try {
      const response = await partiesAPI.list({ q: term, type: 'supplier', limit: 10 });
      setPartyResults(response.data?.parties || response.data || []);
    } catch (error) {
      console.error('Error searching parties:', error);
    }
  };

  const searchItems = async (term) => {
    try {
      const response = await itemsAPI.list({ q: term, limit: 10 });
      setItemResults(response.data?.items || response.data || []);
      setShowItemDropdown(true);
    } catch (error) {
      console.error('Error searching items:', error);
    }
  };

  const selectItem = (item) => {
    const newItem = {
      line_no: formData.items.length + 1,
      item_id: item.id,
      name: item.name,
      code: item.code || '',
      hsn: item.hsn || '',
      quantity: 1,
      unit: item.primary_unit || item.unit || 'PCS',
      unit_price: item.purchase_price || item.cost_price || 0,
      discount: 0,
      tax_rate: item.tax_rate || 18,
      amount: item.purchase_price || item.cost_price || 0
    };
    setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
    setItemSearch('');
    setShowItemDropdown(false);
    toast.success(`${item.name} added`);
  };

  const addBlankItem = () => {
    const newItem = {
      line_no: formData.items.length + 1,
      item_id: null,
      name: '',
      code: '',
      hsn: '',
      quantity: 1,
      unit: 'PCS',
      unit_price: 0,
      discount: 0,
      tax_rate: 18,
      amount: 0
    };
    setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = value;
    const item = updatedItems[index];
    const baseAmount = (item.quantity || 0) * (item.unit_price || 0);
    const discountedAmount = baseAmount - (item.discount || 0);
    const taxAmount = discountedAmount * ((item.tax_rate || 0) / 100);
    item.amount = discountedAmount + taxAmount;
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const deleteItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    updatedItems.forEach((item, idx) => { item.line_no = idx + 1; });
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => {
      return sum + ((item.quantity || 0) * (item.unit_price || 0));
    }, 0);
    const discount_total = formData.items.reduce((sum, item) => sum + (item.discount || 0), 0);
    const taxable_amount = subtotal - discount_total;
    const tax_total = formData.items.reduce((sum, item) => {
      const baseAmount = (item.quantity || 0) * (item.unit_price || 0);
      const discounted = baseAmount - (item.discount || 0);
      const tax = discounted * ((item.tax_rate || 0) / 100);
      return sum + tax;
    }, 0);
    let grand_total = taxable_amount + tax_total;
    let round_off = 0;
    if (enableRoundOff) {
      const rounded = Math.round(grand_total);
      round_off = rounded - grand_total;
      grand_total = rounded;
    }
    setFormData(prev => ({ ...prev, totals: { subtotal, discount_total, taxable_amount, tax_total, round_off, grand_total } }));
  };

  const handleBarcodeScan = async (barcode) => {
    try {
      setShowScanner(false);
      toast.loading('Looking up item...', { id: 'barcode-lookup' });
      const response = await itemsAPI.lookupByBarcode(barcode);
      toast.success('Item found!', { id: 'barcode-lookup' });
      selectItem(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Item not found. Create it now?', { id: 'barcode-lookup' });
        setScannedBarcode(barcode);
        setShowQuickAdd(true);
      } else {
        toast.error('Failed to lookup item', { id: 'barcode-lookup' });
      }
    }
  };

  const handleItemCreated = (newItem) => {
    selectItem(newItem);
    toast.success('Item added to invoice!');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.party) newErrors.party = 'Supplier is required';
    if (formData.items.length === 0) newErrors.items = 'At least one item is required';
    formData.items.forEach((item, index) => {
      if (!item.name) newErrors[`item_${index}_name`] = 'Item name required';
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => ({
    party_id: formData.party?.id || '',
    invoice_number: formData.invoice_no || '',
    supplier_invoice_number: formData.invoice_no || '',
    invoice_date: formData.doc_date,
    due_date: formData.due_date || null,
    place_of_supply: formData.meta?.place_of_supply || '',
    line_items: (formData.items || []).map(item => {
      const qty = item.quantity || 1;
      const rate = item.unit_price || 0;
      const amount = rate * qty;
      const taxAmt = amount * ((item.tax_rate || 0) / 100);
      return {
        item_id: item.item_id || '',
        item_name: item.name || '',
        item_sku: item.code || '',
        hsn_code: item.hsn || '',
        quantity: qty,
        unit: item.unit || 'PCS',
        rate: rate,
        discount_amount: 0,
        tax_rate: item.tax_rate || 0,
        tax_amount: taxAmt,
        amount: amount,
        net_amount: amount + taxAmt,
        total_amount: amount + taxAmt,
      };
    }),
    notes: formData.terms?.notes_public || '',
    terms: (formData.terms?.terms_lines || []).join('\n'),
  });

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors');
      return;
    }
    try {
      setSaveLoading(true);
      const payload = buildPayload();

      if (isEdit) {
        await purchasesAPI.updateInvoice(id, payload);
        toast.success('Invoice updated successfully!');
      } else {
        const response = await purchasesAPI.createInvoice(payload);
        toast.success('Invoice created successfully!');
        setSavedInvoiceId(response.data.id);
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error('Failed to save invoice');
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePrint = () => {
    if (!savedInvoiceId && !id) {
      toast.error('Save invoice first before printing');
      return;
    }
    setShowPrintPreview(true);
  };

  const handleFinalize = async () => {
    let invoiceId = savedInvoiceId || id;
    // Auto-save first if not yet saved
    if (!invoiceId) {
      if (!validateForm()) {
        toast.error('Please fix the errors');
        return;
      }
      try {
        setSaveLoading(true);
        const payload = buildPayload();
        payload.save_as_draft = false;
        const response = await purchasesAPI.createInvoice(payload);
        invoiceId = response.data.id;
        setSavedInvoiceId(invoiceId);
      } catch (error) {
        console.error('Error saving invoice:', error);
        toast.error('Failed to save invoice');
        return;
      } finally {
        setSaveLoading(false);
      }
    }
    try {
      await purchasesAPI.finalizeInvoice(invoiceId);
      toast.success('Invoice finalized!');
      navigate(`/business/${activeBusiness?.id}/purchases/invoices`);
    } catch (error) {
      toast.error('Failed to finalize invoice');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PremiumDocumentLayout
        title={isEdit ? `Edit Purchase Invoice ${formData.invoice_no}` : 'Create Purchase Invoice'}
        subtitle="Record supplier bills and purchases"
        headerActions={
          <DocumentHeaderBar
            backPath={`/business/${activeBusiness?.id}/purchases/invoices`}
            onSave={handleSave}
            onPrint={handlePrint}
            onFinalize={handleFinalize}
            saveLoading={saveLoading}
            saveLabel="Save Draft"
            finalizeLabel="Finalize Invoice"
            showPrint={isEdit || savedInvoiceId}
          />
        }
        leftColumn={
          <>
            <DocumentPartyPicker
              partyType="supplier"
              selectedParty={formData.party}
              onSelectParty={(party) => setFormData(prev => ({ ...prev, party }))}
              onSearch={searchParties}
              searchResults={partyResults}
            />
            <DocumentItemsTable
              items={formData.items}
              onUpdateItem={updateItem}
              onDeleteItem={deleteItem}
              onAddItem={addBlankItem}
              onScanBarcode={() => setShowScanner(true)}
              onSearchItem={(term) => { setItemSearch(term); if (term.length > 1) searchItems(term); }}
              searchTerm={itemSearch}
              searchResults={itemResults}
              showSearchDropdown={showItemDropdown}
              onSelectSearchResult={selectItem}
              errors={errors}
            />
            <Card className="p-4 lg:p-6 border-2 shadow-lg" style={{ borderColor: '#fde68a', background: 'linear-gradient(135deg, #ffffff 0%, #fefce8 100%)' }}>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Terms & Notes</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-gray-600">Internal Notes</Label>
                  <Textarea
                    value={formData.terms.notes_public}
                    onChange={(e) => setFormData(prev => ({ ...prev, terms: { ...prev.terms, notes_public: e.target.value } }))}
                    placeholder="Add internal notes..."
                    className="mt-1 text-sm"
                    rows={3}
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Payment Terms</Label>
                  <select
                    value={formData.meta.payment_terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, meta: { ...prev.meta, payment_terms: e.target.value } }))}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="Net 30">Net 30</option>
                    <option value="Net 15">Net 15</option>
                    <option value="Due on Receipt">Due on Receipt</option>
                    <option value="COD">Cash on Delivery</option>
                  </select>
                </div>
              </div>
            </Card>
          </>
        }
        rightColumn={
          <>
            <Card className="p-4 lg:p-6 border-2 shadow-lg" style={{ borderColor: '#fed7aa', background: 'linear-gradient(135deg, #ffffff 0%, #ffedd5 100%)' }}>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg" style={{ background: 'linear-gradient(135deg, #fed7aa 0%, #fde68a 100%)' }}>
                  <FileText className="w-4 h-4 text-amber-700" />
                </div>
                Invoice Details
              </h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-600">Invoice Number</Label>
                  <Input value={formData.invoice_no} onChange={(e) => setFormData(prev => ({ ...prev, invoice_no: e.target.value }))} className="text-sm font-semibold" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600">Invoice Date</Label>
                    <Input type="date" value={formData.doc_date} onChange={(e) => setFormData(prev => ({ ...prev, doc_date: e.target.value }))} className="text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Due Date</Label>
                    <Input type="date" value={formData.due_date} onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))} className="text-sm" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Reference / PO Number</Label>
                  <Input value={formData.meta.reference_no} onChange={(e) => setFormData(prev => ({ ...prev, meta: { ...prev.meta, reference_no: e.target.value } }))} placeholder="Supplier invoice or PO" className="text-sm" />
                </div>
              </div>
            </Card>
            <DocumentTotalsPanel totals={formData.totals} enableRoundOff={enableRoundOff} onToggleRoundOff={setEnableRoundOff} />
          </>
        }
      />
      {showScanner && <BarcodeScanner onScan={handleBarcodeScan} onClose={() => setShowScanner(false)} title="Scan Item Barcode or QR Code" />}
      {showQuickAdd && <QuickAddItemModal barcode={scannedBarcode} onClose={() => { setShowQuickAdd(false); setScannedBarcode(''); }} onItemCreated={handleItemCreated} />}
      
      {/* Print Preview Modal */}
      {showPrintPreview && (savedInvoiceId || id) && (
        <PrintPreviewModal
          document={{
            ...formData,
            doc_no: formData.invoice_no,
            doc_type: 'PURCHASE_INVOICE'
          }}
          documentType="PURCHASE_INVOICE"
          onClose={() => setShowPrintPreview(false)}
        />
      )}
    </>
  );
};

export default CreatePurchaseInvoice;
